import { useState } from 'react';
import type { ProviderConfig } from '../types';
import {
  loadKeys, saveKey, removeKey, fingerprint, loadProviders, saveProviders, type KeyName,
  DEFAULT_BASE_URL, DEFAULT_TTS_MODEL, DEFAULT_STT_MODEL,
} from '../lib/keys';
import { toast } from '../lib/toast';

// The key vault. Keys are entered once and only ever rendered back as a masked
// fingerprint. Entering a new value replaces; Remove clears it.

export default function Settings({ onClose }: { onClose: () => void }) {
  const [keys, setKeys] = useState(loadKeys());
  const [providers, setProvidersState] = useState<ProviderConfig>(loadProviders());
  const [openaiInput, setOpenaiInput] = useState('');
  const [elevenInput, setElevenInput] = useState('');

  function commitKey(name: KeyName, value: string, clear: () => void) {
    if (!value.trim()) return;
    saveKey(name, value);
    setKeys(loadKeys());
    clear();
    toast(`${name === 'openai' ? 'OpenAI' : 'ElevenLabs'} key saved.`);
  }
  function clearKey(name: KeyName) {
    removeKey(name);
    setKeys(loadKeys());
  }
  function setProvider<K extends keyof ProviderConfig>(k: K, v: ProviderConfig[K]) {
    saveProviders({ [k]: v });
    setProvidersState(loadProviders());
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Settings">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Settings &amp; keys</h2>
        <p className="lead">
          The app works out of the box with a built-in examiner voice — no key needed. Optionally bring your own key
          for a different voice or provider. Keys are stored in this browser only, sent straight to the provider, and
          never shown back to you in full.
        </p>

        <div className="field">
          <label htmlFor="k-openai">API key — voice &amp; transcription (OpenAI or compatible)</label>
          {keys.openai ? (
            <div className="fingerprint">
              <span>{fingerprint(keys.openai)}</span>
              <div className="actions">
                <button className="mini danger" onClick={() => clearKey('openai')}>Remove</button>
              </div>
            </div>
          ) : (
            <div className="input-row">
              <input
                id="k-openai"
                type="password"
                placeholder="sk-…"
                autoComplete="off"
                value={openaiInput}
                onChange={(e) => setOpenaiInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && commitKey('openai', openaiInput, () => setOpenaiInput(''))}
              />
              <button className="mini" onClick={() => commitKey('openai', openaiInput, () => setOpenaiInput(''))}>Save</button>
            </div>
          )}
          <div className="hint">Powers the examiner voice and transcription. Works with OpenAI or any OpenAI-compatible endpoint (set it below). Without a key the app uses your browser's built-in voice and skips transcripts.</div>
        </div>

        <div className="field">
          <label htmlFor="k-eleven">ElevenLabs key — optional, accent-accurate voices</label>
          {keys.elevenlabs ? (
            <div className="fingerprint">
              <span>{fingerprint(keys.elevenlabs)}</span>
              <div className="actions">
                <button className="mini danger" onClick={() => clearKey('elevenlabs')}>Remove</button>
              </div>
            </div>
          ) : (
            <div className="input-row">
              <input
                id="k-eleven"
                type="password"
                placeholder="…"
                autoComplete="off"
                value={elevenInput}
                onChange={(e) => setElevenInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && commitKey('elevenlabs', elevenInput, () => setElevenInput(''))}
              />
              <button className="mini" onClick={() => commitKey('elevenlabs', elevenInput, () => setElevenInput(''))}>Save</button>
            </div>
          )}
          <div className="hint">More natural British / American / Australian voices. Optional.</div>
        </div>

        <div className="field">
          <label htmlFor="tts-choice">Examiner voice source</label>
          <select
            id="tts-choice"
            value={providers.tts}
            onChange={(e) => setProvider('tts', e.target.value as ProviderConfig['tts'])}
          >
            <option value="workers">Built-in examiner voice (no key needed)</option>
            <option value="openai">OpenAI (natural, needs OpenAI key)</option>
            <option value="elevenlabs">ElevenLabs (accent-accurate, needs ElevenLabs key)</option>
            <option value="browser">Browser voice (uses your OS voices)</option>
          </select>
          <div className="hint">If a chosen provider has no key, the app falls back to the built-in voice, then your browser voice.</div>
        </div>

        <details className="endpoint-block">
          <summary>OpenAI-compatible endpoint (advanced)</summary>
          <p className="hint" style={{ marginTop: 10 }}>
            Point the voice &amp; transcription calls at any OpenAI-compatible API (Groq, Together, OpenRouter, Azure, a
            gateway…). Must be an <b>https</b> base URL. Leave as-is to use OpenAI.
          </p>
          <div className="field">
            <label htmlFor="ep-base">Base URL</label>
            <input
              id="ep-base"
              type="text"
              spellCheck={false}
              autoComplete="off"
              placeholder={DEFAULT_BASE_URL}
              value={providers.openaiBaseUrl}
              onChange={(e) => setProvider('openaiBaseUrl', e.target.value)}
              onBlur={(e) => !e.target.value.trim() && setProvider('openaiBaseUrl', DEFAULT_BASE_URL)}
            />
            <div className="hint">e.g. <code>https://api.groq.com/openai/v1</code>. Paths <code>/audio/speech</code> and <code>/audio/transcriptions</code> are appended.</div>
          </div>
          <div className="field" style={{ display: 'flex', gap: 12, marginBottom: 0 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="ep-tts-model">Voice model</label>
              <input id="ep-tts-model" type="text" spellCheck={false} autoComplete="off" placeholder={DEFAULT_TTS_MODEL}
                value={providers.ttsModel}
                onChange={(e) => setProvider('ttsModel', e.target.value)}
                onBlur={(e) => !e.target.value.trim() && setProvider('ttsModel', DEFAULT_TTS_MODEL)} />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="ep-stt-model">Transcription model</label>
              <input id="ep-stt-model" type="text" spellCheck={false} autoComplete="off" placeholder={DEFAULT_STT_MODEL}
                value={providers.sttModel}
                onChange={(e) => setProvider('sttModel', e.target.value)}
                onBlur={(e) => !e.target.value.trim() && setProvider('sttModel', DEFAULT_STT_MODEL)} />
            </div>
          </div>
          <div className="hint" style={{ marginTop: 10 }}>
            Transcription needs a Whisper-compatible model that returns <b>word timestamps</b> (verbose JSON) — otherwise
            metrics are skipped. Note: a hosted endpoint must be reachable from the internet (a service on your own
            machine won't work through this proxy).
          </div>
        </details>

        <p className="egress-note">
          <b>What leaves this device.</b> When you record an answer, that audio is sent to the transcription provider,
          and the question text is sent to the voice provider — both through your own keys. Your recordings, transcripts,
          and metrics are saved only in this browser. Nothing is uploaded to us.
        </p>

        <div className="rv-foot">
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
